import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Form, Button, Alert, InputGroup, Row, Col } from 'react-bootstrap';
import MapComponent, { MapMarker } from './MapComponent';
import SendMessageModal from './SendMessageModal'; // Import the new modal component
import SearcherChat from './SearcherChat'; // Import SearcherChat
import SearcherDashboard from './SearcherDashboard';
import { loQueSeaAFloat } from '../utils/TypeUtils';


interface PlayerSearchProps {
  onSearch: (searchParams: { latitude: number; longitude: number; radius: number }) => void;
  players: any[]; // This will be replaced with a proper Player interface later
  setMessage: (message: { type: 'success' | 'danger'; text: string } | null) => void;
  onSearcherNameChange: (name: string) => void; // Callback to update searcherName in App.tsx
  searcherNameApp: string | null; // Searcher's name from App.tsx
  onSearcherGameAddressChange: (address: string) => void; // Callback to update gameAddress in App.tsx
  onSearcherGameDateChange: (date: string) => void; // Callback to update gameDate in App.tsx
  onSearcherGameTimeChange: (time: string) => void; // Callback to update gameTime in App.tsx
}

export interface SearcherChatWindow {
  recipientId: string;
  recipientName: string;
  gameAddress: string;
  gameDate: string;
  gameTime: string;
}

const PlayerSearch: React.FC<PlayerSearchProps> = ({ onSearch, players, setMessage, onSearcherNameChange, searcherNameApp, onSearcherGameAddressChange, onSearcherGameDateChange, onSearcherGameTimeChange }) => {
  const { t } = useTranslation();
  const [location, setLocation] = useState<{ latitude: number; longitude: number } | null>(null);
  const [radius, setRadius] = useState<number>(5); // Default radius of 5 km
  const [mapCenter, setMapCenter] = useState<[number, number]>([-34.6037, -58.3816]); // Default to Buenos Aires
  const [error, setError] = useState<string | null>(null);
  const [loadingLocation, setLoadingLocation] = useState<boolean>(false);
  const [showSendMessageModal, setShowSendMessageModal] = useState<boolean>(false);
  const [selectedPlayer, setSelectedPlayer] = useState<any>(null);
  const [openSearcherChatWindows, setOpenSearcherChatWindows] = useState<SearcherChatWindow[]>([]);
  const [searcherUniqueId, setSearcherUniqueId] = useState<string | null>(localStorage.getItem('searcherUniqueId'));
  const [searcherName, setSearcherName] = useState<string>(searcherNameApp || ''); // Initialize with searcherNameApp
  const [gameAddress, setGameAddress] = useState<string>(''); // New state for game address
  const [gameDate, setGameDate] = useState<string>(''); // New state for game date
  const [gameTime, setGameTime] = useState<string>(''); // New state for game time
  const [searcherInitiatedChats, setSearcherInitiatedChats] = useState<string[]>([]); // New state to track chats initiated by this searcher
  const [activeChats, setActiveChats] = useState<string[]>([]); // New state to track all active chats for this searcher
  const [hasEnteredSearcherInfo, setHasEnteredSearcherInfo] = useState<boolean>(false); // New state to track if searcher has entered name and game address
  const [hasAutoSearched, setHasAutoSearched] = useState<boolean>(false); // New state to track if auto-search has been performed
  const [maximizedChatId, setMaximizedChatId] = useState<string | null>(null); // New state to track maximized chat

  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

  useEffect(() => {
    console.log('PlayerSearch: Initial useEffect running.');
    console.log('PlayerSearch: searcherUniqueId from state:', searcherUniqueId);
    const storedSearcherName = localStorage.getItem('searcherName');
    const storedGameAddress = localStorage.getItem('gameAddress');
    const storedGameDate = localStorage.getItem('gameDate');
    const storedGameTime = localStorage.getItem('gameTime');
    const storedLocation = localStorage.getItem('location');

    const verifySearcher = async (id: string) => {
      console.log('PlayerSearch: verifySearcher called for ID:', id);
      try {
        console.log('PlayerSearch: Calling /api/searchers/:id/exists for ID:', id);
        const response = await fetch(`${backendUrl}/api/searchers/${id}/exists`);
        console.log('PlayerSearch: /api/searchers/:id/exists response.ok:', response.ok);
        if (response.ok) {
          const data = await response.json();
          console.log('PlayerSearch: /api/searchers/:id/exists data.exists:', data.exists);
          if (!data.exists) {
            console.log(`PlayerSearch: Searcher ID ${id} does not exist in backend. Clearing local storage and state.`);
            localStorage.removeItem('searcherUniqueId');
            setSearcherUniqueId(null); // Clear it to trigger generation if needed
            setSearcherName('');
            onSearcherNameChange(''); // Update App.tsx
            setGameAddress('');
            setLocation(null);
            setHasEnteredSearcherInfo(false);
            localStorage.removeItem('searcherName');
            localStorage.removeItem('gameAddress');
            localStorage.removeItem('gameDate');
            localStorage.removeItem('gameTime');
            localStorage.removeItem('location');
          } else {
            console.log('PlayerSearch: Searcher ID exists in backend. Setting states.');
            // setSearcherUniqueId(id); // Already set from localStorage
            setSearcherUniqueId(id); // Update App.tsx with the verified ID
            if (storedSearcherName) {
              setSearcherName(storedSearcherName);
              onSearcherNameChange(storedSearcherName); // Update App.tsx
            }
            if (storedGameAddress) {
              setGameAddress(storedGameAddress);
              onSearcherGameAddressChange(storedGameAddress); // Update App.tsx
            }
            if (storedGameDate) {
              setGameDate(storedGameDate);
            }
            if (storedGameTime) {
              setGameTime(storedGameTime);
            }
            if (storedLocation) setLocation(JSON.parse(storedLocation));
            if (storedSearcherName && storedGameAddress && storedGameDate && storedGameTime && storedLocation) setHasEnteredSearcherInfo(true);
          }
        } else {
          console.error('PlayerSearch: Error verifying searcher ID with backend (response not ok):', await response.json());
          // If there's an error, assume ID is invalid for safety
          localStorage.removeItem('searcherUniqueId');
          setSearcherUniqueId(null); // Clear it to trigger generation if needed
          setSearcherName('');
          onSearcherNameChange(''); // Update App.tsx
          setGameAddress('');
          setLocation(null);
          setHasEnteredSearcherInfo(false);
          localStorage.removeItem('searcherName');
          localStorage.removeItem('gameAddress');
          localStorage.removeItem('gameDate');
          localStorage.removeItem('gameTime');
          localStorage.removeItem('location');
        }
      } catch (error) {
        console.error('PlayerSearch: Network error verifying searcher ID:', error);
        // If there's a network error, assume ID is invalid for safety
        localStorage.removeItem('searcherUniqueId');
        setSearcherUniqueId(null); // Clear it to trigger generation if needed
        setSearcherName('');
        onSearcherNameChange(''); // Update App.tsx
        setGameAddress('');
        setLocation(null);
        setHasEnteredSearcherInfo(false);
        localStorage.removeItem('searcherName');
        localStorage.removeItem('gameAddress');
        localStorage.removeItem('location');
      }
    };

    if (searcherUniqueId) {
      console.log('PlayerSearch: searcherUniqueId exists, calling verifySearcher.');
      verifySearcher(searcherUniqueId);
    } else {
      console.log('PlayerSearch: No searcherUniqueId in state, will be generated if needed.');
      // No need to call setSearcherUniqueId('') here, the next useEffect will handle generation
    }

    // Set default date to today
    const today = new Date();
    setGameDate(today.toISOString().slice(0, 10)); // YYYY-MM-DD

    // Set default time to the next hour
    const nextHour = new Date(today.setHours(today.getHours() + 1));
    setGameTime(nextHour.toTimeString().slice(0, 5)); // HH:MM

    console.log('PlayerSearch: Attempting to get geolocation.');
    setLoadingLocation(true);
    if (navigator.geolocation) {
      console.log('PlayerSearch: navigator.geolocation is available.');
      navigator.geolocation.getCurrentPosition(
        (position) => {
          console.log('PlayerSearch: Geolocation success:', position.coords.latitude, position.coords.longitude);
          const newLocation = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          };
          setLocation(newLocation);
          setMapCenter([newLocation.latitude, newLocation.longitude]);
          setLoadingLocation(false);
        },
        (err) => {
          console.warn('PlayerSearch: Geolocation failed:', err.message);
          setError(t('geolocationError', { message: err.message }));
          setLoadingLocation(false);
        }
      );
    } else {
      console.log('PlayerSearch: navigator.geolocation is NOT available.');
      setError(t('geolocationNotSupported'));
      setLoadingLocation(false);
    }
  }, []); // Run only once on mount

  // Effect to generate searcherUniqueId if it's missing
  useEffect(() => {
    console.log('PlayerSearch: searcherUniqueId generation useEffect running. Current ID:', searcherUniqueId);
    if (searcherUniqueId === null) {
      console.log('PlayerSearch: Generating new searcherUniqueId.');
      const newId = Math.random().toString(36).substr(2, 9);
      localStorage.setItem('searcherUniqueId', newId);
      setSearcherUniqueId(newId);
    }
  }, [searcherUniqueId]);

  useEffect(() => {
    if (searcherUniqueId) {
      localStorage.setItem('searcherUniqueId', searcherUniqueId);

      const fetchActiveChats = async () => {
        try {
          const response = await fetch(`${backendUrl}/api/searchers/${searcherUniqueId}/active-chat-players`);
          if (response.ok) {
            const data = await response.json();
            setActiveChats(Array.isArray(data.activeChatPlayerIds) ? data.activeChatPlayerIds : []); // Ensure activeChats is always an array
          } else {
            console.error('Error fetching active chats:', await response.json());
          }
        } catch (error) {
          console.error('Network error fetching active chats:', error);
        }
      };
      fetchActiveChats();
    } else {
      localStorage.removeItem('searcherUniqueId');
      setActiveChats([]); // Clear active chats if searcherUniqueId is null
    }
  }, [searcherUniqueId, backendUrl]);

  useEffect(() => {
    if (searcherName) {
      localStorage.setItem('searcherName', searcherName);
      onSearcherNameChange(searcherName); // Update App.tsx
    } else {
      localStorage.removeItem('searcherName');
      onSearcherNameChange(''); // Update App.tsx
      setHasEnteredSearcherInfo(false); // Reset if name is cleared
    }
  }, [searcherName]);

  useEffect(() => {
    if (gameAddress) {
      localStorage.setItem('gameAddress', gameAddress);
    } else {
      localStorage.removeItem('gameAddress');
      setHasEnteredSearcherInfo(false); // Reset if game address is cleared
    }
  }, [gameAddress]);

  useEffect(() => {
    if (gameDate) {
      localStorage.setItem('gameDate', gameDate);
    } else {
      localStorage.removeItem('gameDate');
      setHasEnteredSearcherInfo(false); // Reset if game date is cleared
    }
  }, [gameDate]);

  useEffect(() => {
    if (gameTime) {
      localStorage.setItem('gameTime', gameTime);
    } else {
      localStorage.removeItem('gameTime');
      setHasEnteredSearcherInfo(false); // Reset if game time is cleared
    }
  }, [gameTime]);

  useEffect(() => {
    if (location) {
      setMapCenter([location.latitude, location.longitude]);
    }
  }, [location]);

  useEffect(() => {
    console.log('PlayerSearch: onSearch useEffect running. location:', location, 'hasAutoSearched:', hasAutoSearched);
    // Allow search to happen if location is available, regardless of searcher info completion
    if (location && !hasAutoSearched) {
      console.log('PlayerSearch: Calling onSearch with location:', location, 'and radius:', radius);
      onSearch({ latitude: location.latitude, longitude: location.longitude, radius });
      setHasAutoSearched(true);
    }
  }, [location, radius, onSearch, hasAutoSearched]);

  // Effect to send searcher heartbeat periodically
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (searcherUniqueId && hasEnteredSearcherInfo) {
      const sendHeartbeat = async () => {
        try {
          const response = await fetch(`${backendUrl}/api/searchers/${searcherUniqueId}/heartbeat`, {
            method: 'POST',
          });
          if (!response.ok) {
            console.error('Failed to send searcher heartbeat:', await response.json());
          }
        } catch (error) {
          console.error('Network error sending searcher heartbeat:', error);
        }
      };

      sendHeartbeat(); // Send immediately on mount/update
      interval = setInterval(sendHeartbeat, 30 * 1000); // Send every 30 seconds
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [searcherUniqueId, hasEnteredSearcherInfo, backendUrl]);

  console.log('PlayerSearch: players prop', players); // Debugging line

  const handleSendMessageClick = (player: any) => {
    setSelectedPlayer(player);
    setShowSendMessageModal(true);
  };

  const mapMarkers: MapMarker[] = players.map(player => ({
    id: player.id,
    latitude: player.latitude,
    longitude: player.longitude,
    name: player.name,
    isOnline: player.isOnline,
    type: 'player',
    contact: player.contact,
    hasActiveChat: activeChats.some(id => id === player.id),
    onSendMessageClick: handleSendMessageClick,
    onViewChatClick: (playerId: string) => {
      const player = players.find(p => p.id === playerId);
      if (player) {
        setOpenSearcherChatWindows(prev => {
          if (!prev.some(chat => chat.recipientId === playerId)) {
            return [...prev, { recipientId: playerId, recipientName: player.name, gameAddress: gameAddress, gameDate: gameDate, gameTime: gameTime }];
          }
          return prev;
        });
      }
    },
  }));

  if (location) {
    mapMarkers.push({
      id: 'searcher-location',
      latitude: location.latitude,
      longitude: location.longitude,
      name: 'Your Search Location',
      type: 'location',
    });
  }

  const getContactLink = (contact: string) => {
    if (contact.includes('@')) {
      return <a href={`mailto:${contact}`}>{contact}</a>;
    } else if (/^\+?[0-9\s-()]{7,20}$/.test(contact)) { // Basic phone number regex
      return <a href={`tel:${contact.replace(/\s/g, '')}`}>{contact}</a>;
    } else {
      return contact;
    }
  };

  const getLocation = () => {
    setLoadingLocation(true);
    setError(null);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const newLocation = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          };
          setLocation(newLocation);
          setLoadingLocation(false);
        },
        (err) => {
          setError(t('geolocationError', { message: err.message }));
          setLoadingLocation(false);
        }
      );
    } else {
      setError(t('geolocationNotSupported'));
      setLoadingLocation(false);
    }
  };

  const handleMapClick = (latlng: { lat: number; lng: number }) => {
    setLocation({ latitude: latlng.lat, longitude: latlng.lng });
  };

  const handleSearch = (event: React.FormEvent) => {
    event.preventDefault();
    if (!location) {
      setError(t('locationRequiredForSearch'));
      return;
    }
    if (location && radius) {
      onSearch({ latitude: location.latitude, longitude: location.longitude, radius });
    }
    else {
      setError(t('allFieldsRequiredForSearch'));
    }
  };

  const handleSendMessage = async (recipientId: string, content: string, senderLatitude: number, senderLongitude: number) => {
    if (!searcherUniqueId || !searcherName || !gameAddress || !gameDate || !gameTime) {
      setMessage({ type: 'danger', text: t('searcherInfoMissing') }); // New translation key needed
      setShowSendMessageModal(false);
      return;
    }

    try {
      console.log('Attempting to send message:');
      console.log('  senderName:', searcherName);
      console.log('  gameAddress:', gameAddress);
      console.log('  recipientId:', recipientId);
      console.log('  content:', content);
      console.log('  backendUrl:', backendUrl);
      console.log('  searcherUniqueId (senderId):', searcherUniqueId);

      const senderId = searcherUniqueId; // Use the unique ID for the searcher

      const response = await fetch(`${backendUrl}/api/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ senderId, recipientId, content, gameAddress, gameDate, gameTime, senderLatitude, senderLongitude, searcherId: searcherUniqueId, senderName: searcherName }),
      });

      const data = await response.json();

      if (response.ok) {
        setMessage({ type: 'success', text: t('messageSentSuccess') });
        setOpenSearcherChatWindows(prev => {
          if (!prev.some(chat => chat.recipientId === recipientId)) {
            return [...prev, { recipientId, recipientName: selectedPlayer.name, gameAddress, gameDate, gameTime }];
          }
          return prev;
        });
        console.log('Message sent successfully:', data.message);
        // setShowSearcherChat(true); // Automatically open chat after sending message
      } else {
        setMessage({ type: 'danger', text: t('messageSentError', { message: data.message || 'Unknown error' }) });
        console.error('Error sending message:', data.message);
      }
    } catch (error: any) {
      setMessage({ type: 'danger', text: t('messageSentError', { message: error.message }) });
      console.error('Network error sending message:', error);
    }
    setShowSendMessageModal(false); // Close modal after attempting to send
  };

  const handleCloseSendMessageModal = () => {
    setShowSendMessageModal(false);
    setSelectedPlayer(null);
  };

  const handleMaximizeToggle = (chatId: string | null) => {
    setMaximizedChatId(chatId);
  };

  const handleConfirmSearcherInfo = async () => {
    if (!searcherName || !gameAddress || !gameDate || !gameTime) {
      setError(t('fillAllFields')); // Using a more general message
      return;
    }
    if (!location) {
      setError(t('getLocationFirst'));
      return;
    }
    if (!searcherUniqueId) {
      setError(t('searcherIdNotAvailable')); // New translation key needed
      return;
    }

    // Persist searcher data to backend
    try {
      const response = await fetch(`${backendUrl}/api/searchers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: searcherUniqueId,
          name: searcherName,
          contact: '', // Assuming contact is not collected for searchers here
          latitude: location.latitude,
          longitude: location.longitude,
          gameAddress: gameAddress,
          gameDate: gameDate,
          gameTime: gameTime,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setMessage({ type: 'success', text: t('searcherInfoSaved') }); // New translation key needed
        setHasEnteredSearcherInfo(true);
        localStorage.setItem('location', JSON.stringify(location)); // Persist location
        onSearcherGameAddressChange(gameAddress); // Update gameAddress in App.tsx
        onSearcherGameDateChange(gameDate); // Update gameDate in App.tsx
        onSearcherGameTimeChange(gameTime); // Update gameTime in App.tsx
        // No need to store searcherName, gameAddress, gameDate, gameTime in localStorage here,
        // as they are already in state and will be used for subsequent API calls.
      } else {
        setMessage({ type: 'danger', text: t('searcherInfoSaveError', { message: data.message || 'Unknown error' }) }); // New translation key needed
        console.error('Error saving searcher info:', data.message);
      }
    } catch (error: any) {
      setMessage({ type: 'danger', text: t('searcherInfoSaveError', { message: error.message }) });
      console.error('Network error saving searcher info:', error);
    }
  };

  return (
    <>
      <Form onSubmit={handleSearch}>
        {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}

        {!hasEnteredSearcherInfo && (
          <>
            <Form.Group className="mb-3">
              <Form.Label>{t('yourName')}</Form.Label>
              <Form.Control
                type="text"
                placeholder={t('enterYourName')}
                value={searcherName}
                onChange={(e) => setSearcherName(e.target.value)}
              />
            </Form.Group>

            <Form.Group className="mb-3">
            </Form.Group>

            <Row className="mb-3">
              <Col md={6}>
                <Form.Group controlId="formGameDate">
                  <Form.Label>{t('gameDate')}</Form.Label>
                  <Form.Control
                    type="date"
                    value={gameDate}
                    onChange={(e) => setGameDate(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId="formGameTime">
                  <Form.Label>{t('gameTime')}</Form.Label>
                  <Form.Control
                    type="time"
                    value={gameTime}
                    onChange={(e) => setGameTime(e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>{t('gameAddress')}</Form.Label>
              <InputGroup>
                <Form.Control
                  type="text"
                  placeholder={t('enterGameAddress')}
                  value={gameAddress}
                  onChange={(e) => setGameAddress(e.target.value)}
                />
                <Button variant="info" onClick={getLocation} disabled={loadingLocation}>
                  {loadingLocation ? t('gettingLocation') : t('getLocation')}
                </Button>
                {location && (
                  <Form.Text className="ms-2 text-success" hidden>
                    {t('locationSelected', { lat: loQueSeaAFloat(location.latitude).toFixed(2), lon: loQueSeaAFloat(location.longitude).toFixed(2) })}
                  </Form.Text>
                )}
                <Button variant="primary" onClick={handleConfirmSearcherInfo} disabled={!searcherUniqueId}>
                  {t('confirm')}
                </Button>
              </InputGroup>
            </Form.Group>
          </>
        )}

        {hasEnteredSearcherInfo && location && (
          <Form.Group className="mb-3">
            <Form.Text className="ms-2 text-success">
              {t('locationSelected', { lat: loQueSeaAFloat(location.latitude).toFixed(2), lon: loQueSeaAFloat(location.longitude).toFixed(2) })}
            </Form.Text>
          </Form.Group>
        )}

        <Row className="mb-3 align-items-center">
          <Col xs={12} md={8}>
            <Form.Group controlId="formRadius">
              <Form.Label>{t('searchRadius')}: {radius} km</Form.Label>
              <Form.Range
                value={radius}
                onChange={(e) => setRadius(Number(e.target.value))}
                min="1"
                max="20"
                step="1"
              />
            </Form.Group>
          </Col>
          <Col xs={12} md={4} className="d-grid gap-2">
            <Button variant="primary" type="submit">
              {t('findPlayers')}
            </Button>
          </Col>
        </Row>

        <div className="mb-3 mt-4">
          <MapComponent
            center={location ? [location.latitude, location.longitude] : mapCenter}
            zoom={13}
            onMapClick={hasEnteredSearcherInfo && location ? undefined : handleMapClick}
            markers={mapMarkers}
            searcherUniqueId={searcherUniqueId}
            searcherName={searcherName}
            gameAddress={gameAddress}
            searchRadiusCircle={location && radius ? { center: location, radius: radius } : undefined}
          >
          </MapComponent>
        </div>

        {players.length === 0 && <Alert variant="info">{t('noPlayersFound')}</Alert>}

        {selectedPlayer && (
          <SendMessageModal
            show={showSendMessageModal}
            onHide={handleCloseSendMessageModal}
            player={selectedPlayer}
            onSend={handleSendMessage}
            senderLatitude={location?.latitude || 0}
            senderLongitude={location?.longitude || 0}
            searcherUniqueId={searcherUniqueId}
            gameAddress={gameAddress} // Pass gameAddress from state
            gameDate={gameDate} // Pass gameDate from state
            gameTime={gameTime} // Pass gameTime from state
            recipientName={selectedPlayer.name}
          />
        )}

        {openSearcherChatWindows.length > 0 && searcherUniqueId && (
          <div className="player-chat-windows-container">
            {openSearcherChatWindows.map(chat => (
              <div key={chat.recipientId} className="chat-window">
                <SearcherChat
                  searcherId={searcherUniqueId}
                  recipientId={chat.recipientId}
                  searcherLatitude={location?.latitude || 0}
                  searcherLongitude={location?.longitude || 0}
                  searcherName={searcherName}
                  gameAddress={chat.gameAddress}
                  gameDate={chat.gameDate}
                  gameTime={chat.gameTime}
                  onCloseChat={() => setOpenSearcherChatWindows(prev => prev.filter(c => c.recipientId !== chat.recipientId))}
                  isMaximized={maximizedChatId === chat.recipientId}
                  onMaximizeToggle={handleMaximizeToggle}
                  unreadCount={0} // PlayerSearch doesn't track unread counts for searcher chats
                  onNewMessage={() => {}} // PlayerSearch doesn't handle new messages for searcher chats
                />
              </div>
            ))}
          </div>
        )}

        
      </Form>
      <SearcherDashboard
        currentSearcherId={searcherUniqueId || ''}
        currentSearcherLatitude={location?.latitude || 0}
        currentSearcherLongitude={location?.longitude || 0}
        currentSearcherName={searcherName}
      />
    </>
  );
};

export default PlayerSearch;
